import type { NewCoreIconProps } from '@atlaskit/icon';
import {
	createEditorToolbarActions,
	type ManifestEditorToolbarActions,
} from '@atlaskit/native-embeds-common';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import {
	type Experience,
	type ExperienceManifest,
	experienceManifestId,
	type ExperienceManifestId,
	type LoadingComponentProps,
	type NativeEmbedChildCommand,
} from '@atlassian/native-embeds-core/types';

import {
	dbSharedManifestFields,
	dbSharedUiConfig,
	dbToolbarItems,
	getDbEntryMoreItems,
} from './shared';

const isDbEntryV2Enabled = (): boolean =>
	expValEqualsNoExposure('cc-maui-experiment', 'isEnabled', true) &&
	expValEquals('databases-native-embeds-v2', 'isEnabled', true);

/**
 * Returns the parameter defaults for the DB Entry embed.
 *
 * When v2 is enabled (see {@link isDbEntryV2Enabled}), new entry embeds
 * default to having the title visible (`alwaysShowTitle: true`),
 * matching the regular DB embed behaviour. Otherwise no override is
 * applied.
 */
const getDbEntryParameterDefaults = (): { alwaysShowTitle?: true } =>
	isDbEntryV2Enabled() ? { alwaysShowTitle: true } : {};

export const DB_ENTRY_EXPERIENCE_MANIFEST_ID: ExperienceManifestId =
	experienceManifestId('database-entry');

const isDatabaseEntryUrl = (url: string): boolean => {
	try {
		return (
			url.match(/\/wiki\/spaces\/~?[\d\w]+\/database\/\d+(\?.*)?$/) !== null &&
			new URL(url).searchParams.has('entryId')
		);
	} catch {
		return false;
	}
};

/**
 * Experience manifest for a DB entry embed (URL contains an entryId param).
 *
 * When the `databases-native-embeds-v2` experiment is enabled (gated by
 * the {@link isDbEntryV2Enabled} `cc-maui-experiment` pre-check):
 * - The "Always show title" toolbar toggle becomes available in the More-options
 *   dropdown (see {@link getDbEntryMoreItems}).
 * - New entry embeds default to having the title visible (see
 *   {@link getDbEntryParameterDefaults}).
 * - The embed renders with a border (see {@link isDbEntryV2Enabled}).
 *
 * `editorToolbarActions`, `parameterDefaults`, and `uiConfig.showBorder` are
 * evaluated lazily (thunk / getter) so the experiment is checked at
 * toolbar-build / auto-convert / render time, not at module-load time when
 * Statsig may not yet be bootstrapped.
 *
 * Must be registered before DBExperienceManifest so that the more
 * specific URL match is evaluated first.
 */
export const DBEntryExperienceManifest: {
	readonly handleChildCommands: (
		_experience: Experience,
		_command: NativeEmbedChildCommand,
	) => void;
	readonly id: ExperienceManifestId;
	readonly isActive: () => true;
	readonly isUrlMatch: (url: string) => boolean;
	readonly lockResizeAspectRatio: () => false;
	readonly parameterDefaults: { alwaysShowTitle?: true };
	readonly shouldAutoConvertToNativeEmbed: true;
	readonly supportedFeatures: Set<string>;
	readonly transformUrl: (url: string, _isFullScreen?: boolean) => string;
	readonly uiConfig: {
		readonly appearance: 'minimal' | 'overlay';
		readonly editorToolbarActions: () => ManifestEditorToolbarActions<any>;
		readonly editorToolbarHandlers: {};
		readonly renderLoadingComponent: (props: LoadingComponentProps) => JSX.Element;
		readonly showBorder: boolean;
		readonly showHeader: false;
		readonly titleIcon: {
			(props: NewCoreIconProps): JSX.Element;
			displayName: string;
		};
	};
} = {
	...dbSharedManifestFields,
	id: DB_ENTRY_EXPERIENCE_MANIFEST_ID,
	isUrlMatch: isDatabaseEntryUrl,
	get parameterDefaults() {
		return getDbEntryParameterDefaults();
	},
	uiConfig: {
		...dbSharedUiConfig,
		get appearance() {
			return isDbEntryV2Enabled() ? 'overlay' : 'minimal';
		},
		get showBorder() {
			return isDbEntryV2Enabled();
		},
		editorToolbarActions: () =>
			createEditorToolbarActions({
				items: [...dbToolbarItems],
				moreItems: getDbEntryMoreItems(),
			}),
	},
} satisfies ExperienceManifest;
